import { APPROVAL_COMMENT_MAX } from '@servicetim/shared';
import { useDecideApproval } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ProblemBanner,
  Textarea,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';

export interface ApprovalDecisionTarget {
  ticketId: string;
  approvalId: string;
  decision: 'approved' | 'rejected';
  ticketKey?: string | null;
}

/** Optional-comment confirm for one approve/reject decision (S-07-09), shared by every screen. */
export function ApprovalDecisionDialog({
  target,
  onClose,
}: {
  target: ApprovalDecisionTarget | null;
  onClose: () => void;
}) {
  const id = useId();
  const decide = useDecideApproval();
  const [comment, setComment] = useState('');
  const open = target !== null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the draft when a new target opens
  useEffect(() => {
    if (!open) return;
    setComment('');
    decide.reset();
  }, [open, target?.approvalId]);

  if (!target) return null;
  const verb = target.decision === 'approved' ? 'Approve' : 'Reject';

  const submit = async () => {
    try {
      await decide.mutateAsync({
        ticketId: target.ticketId,
        approvalId: target.approvalId,
        decision: target.decision,
        comment: comment.trim() === '' ? undefined : comment.trim(),
      });
      toast.success(
        `${verb === 'Approve' ? 'Approved' : 'Rejected'}${target.ticketKey ? ` ${target.ticketKey}` : ''}`,
      );
      onClose();
    } catch {
      // banner
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>
            {verb} {target.ticketKey ?? 'this request'}?
          </DialogTitle>
          <DialogDescription>
            An optional comment is visible to everyone on the request.
          </DialogDescription>
        </DialogHeader>
        {decide.error ? <ProblemBanner error={decide.error} size="sm" /> : null}
        <Textarea
          aria-label="Comment (optional)"
          id={`${id}-comment`}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={APPROVAL_COMMENT_MAX}
          rows={3}
          placeholder="Comment (optional)"
          autoFocus
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={decide.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={target.decision === 'rejected' ? 'destructive' : 'default'}
            onClick={() => void submit()}
            disabled={decide.isPending}
          >
            {verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
